import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    X, Calendar, MapPin,
    DollarSign, TrendingUp, Eye, Printer, Mail,
    User, Smartphone, Phone
} from 'lucide-react';
import './CommonStyles.css';
import './MobileResponsive.css'; // Import mobile responsive styles
import ViewPaymentsModal from './ViewPaymentsModal';
import AddPaymentModal from './AddPaymentModal';
import ViewSaleModal from './ViewSaleModal';
import EditShippingModal from './EditShippingModal';
import SellReturnModal from './SellReturnModal';
import apiService from '../../services/api';
import invoiceGenerator from '../../services/invoiceGenerator';
import UpdatePurchaseStatusModal from './UpdatePurchaseStatusModal';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';
import ViewPurchaseModal from './ViewPurchaseModal';
import dateUtils from '../../utils/dateUtils';

// ─── Transform real API data into UI structure ──────────────────────────────
const buildLedgerData = (apiData, contact) => {
    const ledger = apiData?.ledger || [];
    const isSupplier = contact?.type === 'supplier' || contact?.type === 'both';

    const detailedRows = ledger.map(entry => ({
        date: entry.date || '',
        refNo: entry.ref_no || entry.invoice_no || '',
        type: entry.type || entry.transaction_type || '',
        location: entry.location || '',
        status: entry.payment_status || '',
        debit: Number(entry.debit) || 0,
        credit: Number(entry.credit) || 0,
        balance: typeof entry.balance === 'string'
            ? parseFloat(entry.balance.replace(/[^\d.]/g, '')) || 0
            : (Number(entry.balance) || 0),
        method: entry.payment_method || '',
        others: entry.others || '',
    }));

    const statementRows = ledger.map(entry => {
        const isPayment = String(entry.type).toLowerCase().includes('payment') || !!entry.payment_id;
        const amount = Number(entry.final_total) || Number(entry.debit) || Number(entry.credit) || 0;
        return {
            date: entry.date || '',
            transaction: entry.ref_no || entry.invoice_no || entry.type || '',
            due_date: entry.due_date || null,
            amount: amount,
            balance: typeof entry.balance === 'string'
                ? parseFloat(entry.balance.replace(/[^\d.]/g, '')) || 0
                : (Number(entry.balance) || 0),
            type: entry.type,
            hasViewPayment: isPayment || !!entry.transaction_id,
            transaction_id: entry.transaction_id,
            payment_id: entry.payment_id,
        };
    });

    const transactions = ledger
        .filter(e => (Number(e.debit) > 0 || Number(e.final_total) > 0) && (e.transaction_id || e.sell_lines || e.purchase_lines))
        .map(e => ({
            date: e.date,
            refNo: e.ref_no || e.invoice_no || '',
            type: e.type || 'Sale',
            total: Number(e.final_total) || Number(e.debit) || Number(e.credit) || 0,
            products: (e.sell_lines || e.purchase_lines || []).map(line => ({
                name: line.product?.name || line.product_name || `Product #${line.product_id}`,
                sku: line.variations?.sub_sku || line.product?.sku || line.sub_sku || '--',
                qty: Number(line.quantity || 0),
                unitPrice: Number(line.unit_price_inc_tax || line.unit_price || line.purchase_price || 0),
                discount: Number(line.line_discount_amount || 0),
                tax: Number(line.item_tax || line.tax_id || 0),
                subtotal: Number(line.quantity || 0) * Number(line.unit_price_inc_tax || line.unit_price || line.purchase_price || 0)
            })),
            transaction_id: e.transaction_id,
        }));

    const customerNameStr = (typeof contact?.name === 'object' ? contact?.name?.name : contact?.name) || contact?.supplier_business_name || (typeof apiData?.customer === 'object' ? apiData?.customer?.name : apiData?.customer) || 'Contact';

    return {
        customer: customerNameStr,
        address: [contact?.address_line_1, contact?.city, contact?.state, contact?.country].filter(Boolean).join(', ') || '',
        email: contact?.email || '',
        mobile: contact?.mobile || '',
        landline: contact?.landline || '',
        alternateContact: contact?.alternate_number || '',
        dob: contact?.dob || '',
        customField1: contact?.custom_field1 || '',
        customField2: contact?.custom_field2 || '',
        customField3: contact?.custom_field3 || '',
        customField4: contact?.custom_field4 || '',
        taxNumber: contact?.tax_number || '',
        storeName: contact?.business?.name || (typeof contact?.location === 'object' ? contact?.location?.name : contact?.location) || "Store",
        storeAddress: contact?.business?.business_address || '',
        invoiceNo: '',
        openingBalance: Number(apiData?.beginning_balance) || 0,
        totalInvoice: Number(apiData?.total_invoice) || 0,
        totalPurchase: Number(apiData?.total_purchase) || 0,
        totalPaid: Number(apiData?.total_paid) || 0,
        advanceBalance: (Number(contact?.balance || 0) - Number(apiData?.total_reverse_payment || 0)),
        balanceDue: Number(apiData?.balance_due) || 0,
        detailedRows,
        statementRows,
        transactions,
    };
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────
const generateDummyLedger = (sale, currentContact) => {
    // Determine dynamic values from the 'sale' object or selected contact
    // Matching AllSell.jsx property names: invoiceNo, finalTotal, sellDue, totalPaid
    const invNo = sale?.invoiceNo || sale?.invoice || '84_2025422711';

    // Total from sale or default (finalTotal)
    const totalInv = parseFloat(sale?.finalTotal || sale?.totalPayable || sale?.total || 60.48);

    // Due from sale (sellDue)
    const balDue = parseFloat(sale?.sellDue !== undefined ? sale.sellDue : (sale?.due !== undefined ? sale.due : 60.48));

    // Paid from sale or calculation (totalPaid)
    const totPaid = sale?.totalPaid !== undefined ? parseFloat(sale.totalPaid) : (totalInv - balDue);

    const loc = (typeof sale?.location === 'object' ? sale?.location?.name : sale?.location) || sale?.location_name || localStorage.getItem('etpos_business_name') || "Main Location";

    // Use selected contact info if available, otherwise fallback to sale/dummy
    const cust = (typeof currentContact?.name === 'object' ? currentContact?.name?.name : currentContact?.name) || (typeof sale?.customer === 'object' ? sale?.customer?.name : sale?.customer) || sale?.contact?.name || 'Walk-In Customer';
    const addr = currentContact?.address_line_1
        ? `${currentContact.address_line_1}\n${currentContact.city || ''}, ${currentContact.state || ''}, ${currentContact.country || ''}`
        : 'Flat 402, Green Residency,\nBandra West,\nMumbai, Maharashtra, India,\n400050';

    return {
        customer: cust,
        address: addr,
        email: currentContact?.email || 'anjali.patel@example.com',
        mobile: currentContact?.mobile || '9988776655',
        landline: currentContact?.landline || '0223344556',
        alternateContact: currentContact?.alternate_number || '9123456789',
        dob: currentContact?.dob || '05/20/1988',
        customField1: currentContact?.custom_field1 || 'Preferred Customer',
        customField2: currentContact?.custom_field2 || 'Loyalty Card 9988',
        customField3: currentContact?.custom_field3 || 'Referred by Rajesh',
        customField4: currentContact?.custom_field4 || 'Special Discount',
        taxNumber: currentContact?.tax_number || 'PAN12345K',
        storeName: loc,
        storeAddress: '14199 E HIGHWAY 12 ROGERS,, Little Rock, AR\nUSA, 72756-8',
        invoiceNo: invNo,
        openingBalance: 0.00,
        totalInvoice: totalInv,
        totalPaid: totPaid,
        advanceBalance: 0.00,
        balanceDue: balDue,

        // Format 1 — Detailed Ledger
        detailedRows: [
            { date: sale?.saleDate || '2026-01-01', refNo: invNo, type: 'Sale', location: loc, status: sale?.paymentStatus || 'Partial', debit: totalInv, credit: 0, balance: totalInv, method: '', others: '' },
            { date: '2026-01-05', refNo: 'PAY-0012', type: 'Payment', location: loc, status: 'Paid', debit: 0, credit: totPaid, balance: balDue, method: 'Cash', others: '' },
        ],

        // Format 2 — Statement View
        statementRows: [
            { date: sale?.saleDate || '2026-01-01', transaction: `Invoice ${invNo}`, amount: totalInv, balance: totalInv, type: 'debit' },
            // Always show Payment row so "View Payment" is available for demo
            { date: '2026-01-05', transaction: 'Payment Received', amount: totPaid, balance: balDue, type: 'credit', hasViewPayment: true },
        ],

        // Format 3 — Detailed Transactions + Products
        transactions: [
            {
                date: sale?.saleDate || '2026-01-01',
                refNo: invNo,
                type: 'Sale',
                total: totalInv,
                products: [
                    { name: 'Product A', sku: 'SKU-001', qty: 2, unitPrice: (totalInv / 2), discount: 0.00, tax: 0.00, subtotal: totalInv },
                ]
            }
        ]
    };
};

// ─── Shared UI Components ─────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    // Normalize status to capitalize first letter (e.g. "paid" -> "Paid")
    const normalizedStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';

    const map = {
        Paid: { bg: '#dcfce7', color: '#16a34a' },
        Partial: { bg: '#fef9c3', color: '#ca8a04' },
        Due: { bg: '#fee2e2', color: '#dc2626' },
        Sale: { bg: '#dbeafe', color: '#2563eb' },
        Payment: { bg: '#ede9fe', color: '#7c3aed' },
    };
    const style = map[normalizedStatus] || { bg: 'var(--etpos-border)', color: 'var(--etpos-text-muted)' };
    return (
        <span style={{ background: style.bg, color: style.color, borderRadius: '20px', padding: '0.2rem 0.65rem', fontSize: '0.75rem', fontWeight: '700' }}>
            {normalizedStatus || status}
        </span>
    );
};

// ─── Format 1: Detailed Ledger ────────────────────────────────────────────────
const Format1Table = ({ rows, openingBalance = 0, startDate = '—' }) => {
    const { formatCurrency } = useBusinessSettings();
    return (
        <div style={{ overflowX: 'auto', background: 'var(--etpos-card)', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                    <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid #e2e8f0' }}>
                        {['Date', 'Reference No', 'Type', 'Location', 'Payment Status', 'Debit', 'Credit', 'Balance', 'Payment Method', 'Others'].map(h => (
                            <th key={h} style={{ padding: '0.85rem 1rem', textAlign: 'left', color: 'var(--etpos-text)', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* Opening Balance Row */}
                    <tr style={{ borderBottom: '1px solid #f1f5f9', background: 'var(--etpos-bg)' }}>
                        <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)' }}>{startDate}</td>
                        <td style={{ padding: '0.85rem 1rem' }}></td>
                        <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text)', fontWeight: '600' }}>Opening Balance</td>
                        <td colSpan={2}></td>
                        <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)' }}>{openingBalance > 0 ? formatCurrency(openingBalance) : '—'}</td>
                        <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)' }}>{openingBalance < 0 ? formatCurrency(Math.abs(openingBalance)) : '—'}</td>
                        <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text)', fontWeight: '700' }}>{formatCurrency(openingBalance)}</td>
                        <td colSpan={2}></td>
                    </tr>
                    {rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: 'var(--etpos-card)' }}>
                            <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)', whiteSpace: 'nowrap' }}>{row.date}</td>
                            <td style={{ padding: '0.85rem 1rem', color: '#6366f1', fontWeight: '600' }}>{row.refNo}</td>
                            <td style={{ padding: '0.85rem 1rem' }}><StatusBadge status={row.type} /></td>
                            <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)' }}>{(row.location && typeof row.location === 'object' ? row.location.name : row.location) || '--'}</td>
                            <td style={{ padding: '0.85rem 1rem' }}><StatusBadge status={row.status} /></td>
                            <td style={{ padding: '0.85rem 1rem', color: row.debit > 0 ? '#dc2626' : 'var(--etpos-text-muted)', fontWeight: '600' }}>
                                {row.debit > 0 ? formatCurrency(row.debit) : '—'}
                            </td>
                            <td style={{ padding: '0.85rem 1rem', color: row.credit > 0 ? '#16a34a' : 'var(--etpos-text-muted)', fontWeight: '600' }}>
                                {row.credit > 0 ? formatCurrency(row.credit) : '—'}
                            </td>
                            <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text)', fontWeight: '700' }}>{formatCurrency(row.balance)}</td>
                            <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)' }}>{row.method}</td>
                            <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)' }}>
                                <span dangerouslySetInnerHTML={{ __html: row.others || '--' }} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── Format 2: Statement View ─────────────────────────────────────────────────
const Format2Table = ({ rows, onViewPayment }) => {
    const { formatCurrency } = useBusinessSettings();
    return (
        <div style={{ overflowX: 'auto', background: 'var(--etpos-card)', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                    <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid #e2e8f0' }}>
                        {['Date', 'Transaction', 'Amount', 'Balance', 'View Payment'].map(h => (
                            <th key={h} style={{ padding: '0.85rem 1rem', textAlign: 'left', color: 'var(--etpos-text)', fontWeight: '700' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>
                                No data available in table
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: 'var(--etpos-card)' }}>
                                <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text-muted)', whiteSpace: 'nowrap' }}>{row.date}</td>
                                <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text)', fontWeight: '600' }}>
                                    <div>{row.transaction}</div>
                                    {row.due_date && (
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>
                                            Due {row.due_date}
                                        </div>
                                    )}
                                </td>
                                <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text)', fontWeight: '700' }}>
                                    {formatCurrency(row.amount)}
                                </td>
                                <td style={{ padding: '0.85rem 1rem', color: 'var(--etpos-text)', fontWeight: '700' }}>{formatCurrency(row.balance)}</td>
                                <td style={{ padding: '0.85rem 1rem' }}>
                                    <button
                                        onClick={() => onViewPayment && onViewPayment(row)}
                                        style={{ background: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '0.35rem 0.85rem', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                                    >
                                        <Eye size={13} /> View
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

// ─── Format 3: Detailed Transaction with Inline Products ──────────────────────
const Format3Table = ({ rows, dateFrom, dateTo, salesData = [], purchasesData = [] }) => {
    const { formatCurrency } = useBusinessSettings();
    const ledgerRows = Array.isArray(rows) ? rows : [];
    const [resolvedProducts, setResolvedProducts] = useState({});

    useEffect(() => {
        if (!ledgerRows.length) return;

        const idsToFetch = new Set();
        ledgerRows.forEach(row => {
            let rawLines = row.sell_lines || row.purchase_lines || row.products || [];
            if (typeof rawLines === 'object' && !Array.isArray(rawLines)) {
                rawLines = Object.values(rawLines);
            }
            (rawLines || []).forEach(line => {
                const prodName = line.name || line.product_name || line.product?.name || '';
                const targetId = line.variation_id || line.product_id;
                if (targetId && (!prodName || String(prodName).startsWith('Product #')) && !resolvedProducts[targetId]) {
                    idsToFetch.add(targetId);
                }
            });
        });

        if (idsToFetch.size > 0) {
            const idsArr = Array.from(idsToFetch);
            apiService.getVariations(idsArr.join(','))
                .then(resp => {
                    const list = resp.data || resp || [];
                    const map = {};
                    if (Array.isArray(list)) {
                        list.forEach(v => {
                            const pName = v.product_name || v.name || '';
                            const vName = v.variation_name && v.variation_name !== 'DUMMY' ? v.variation_name : '';
                            const sku = v.sub_sku || v.sku || '';
                            const brand = v.brand_name || '';

                            let fullName = pName;
                            if (vName) fullName += ' - ' + vName;
                            if (sku && !fullName.includes(sku)) fullName += ' ' + sku;
                            if (brand) fullName += ' , ' + brand;

                            const entryKey = v.variation_id || v.id || v.product_id;
                            if (entryKey) {
                                map[entryKey] = {
                                    name: fullName,
                                    sku: sku
                                };
                            }
                        });
                    }
                    if (Object.keys(map).length > 0) {
                        setResolvedProducts(prev => ({ ...prev, ...map }));
                    }
                })
                .catch(() => {});
        }
    }, [ledgerRows]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {/* Header Banner matching Web */}
            <div style={{
                background: '#2d6a9f',
                color: '#ffffff',
                textAlign: 'center',
                padding: '0.65rem 1rem',
                borderRadius: '6px 6px 0 0',
                fontWeight: '700',
                fontSize: '0.9rem'
            }}>
                Showing all invoices and payments between {dateFrom || '01/01/2025'} and {dateTo || '12/31/2025'}
            </div>

            <div style={{ overflowX: 'auto', background: 'var(--etpos-card)', borderRadius: '0 0 8px 8px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ background: '#337ab7', color: '#ffffff', textAlign: 'left', borderBottom: '2px solid #2e6da4' }}>
                            <th style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>Date</th>
                            <th style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>Reference No</th>
                            <th style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>Type</th>
                            <th style={{ padding: '0.65rem 0.85rem' }}>Location</th>
                            <th style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>Payment Status</th>
                            <th style={{ padding: '0.65rem 0.85rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Debit</th>
                            <th style={{ padding: '0.65rem 0.85rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Credit</th>
                            <th style={{ padding: '0.65rem 0.85rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Balance</th>
                            <th style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>Payment Method</th>
                            <th style={{ padding: '0.65rem 0.85rem' }}>Others</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ledgerRows.length === 0 ? (
                            <tr>
                                <td colSpan={10} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                    No data available in table
                                </td>
                            </tr>
                        ) : (
                            ledgerRows.map((row, idx) => {
                                const txnId = row.transaction_id || row.id;
                                const refNo = row.ref_no || row.invoice_no || row.transaction;

                                const matchingSale = (salesData || []).find(s =>
                                    (txnId && String(s.id) === String(txnId)) ||
                                    (refNo && (s.invoice_no === refNo || s.invoiceNo === refNo))
                                );

                                const matchingPurchase = (purchasesData || []).find(p =>
                                    (txnId && String(p.id) === String(txnId)) ||
                                    (refNo && (p.ref_no === refNo || p.refNo === refNo))
                                );

                                let productLines = row.product_lines || row.sell_lines || row.purchase_lines || row.products || [];

                                // Fallback to matching sale / purchase lines if productLines is missing product details
                                if ((!productLines || productLines.length === 0 || !productLines[0]?.name || String(productLines[0]?.name).startsWith('Product #')) && matchingSale?.product_lines?.length > 0) {
                                    productLines = matchingSale.product_lines;
                                } else if ((!productLines || productLines.length === 0 || !productLines[0]?.name || String(productLines[0]?.name).startsWith('Product #')) && matchingSale?.sell_lines?.length > 0) {
                                    productLines = matchingSale.sell_lines;
                                } else if ((!productLines || productLines.length === 0 || !productLines[0]?.name || String(productLines[0]?.name).startsWith('Product #')) && matchingPurchase?.purchase_lines?.length > 0) {
                                    productLines = matchingPurchase.purchase_lines;
                                }

                                const hasProducts = Array.isArray(productLines) && productLines.length > 0;
                                const isEven = idx % 2 === 0;

                                return (
                                    <React.Fragment key={`f3-row-${row.id || ''}-${row.transaction_id || ''}-${row.payment_id || ''}-${row.ref_no || ''}-${idx}`}>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: isEven ? '#ffffff' : '#f9fafb' }}>
                                            <td style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>{row.date || '--'}</td>
                                            <td style={{ padding: '0.65rem 0.85rem', color: '#6366f1', fontWeight: 600 }}>{row.ref_no || row.invoice_no || '--'}</td>
                                            <td style={{ padding: '0.65rem 0.85rem', fontWeight: 600 }}>{row.type || row.transaction_type || '--'}</td>
                                            <td style={{ padding: '0.65rem 0.85rem' }}>{(typeof row.location === 'object' ? row.location?.name : row.location) || row.location_name || '--'}</td>
                                            <td style={{ padding: '0.65rem 0.85rem' }}>
                                                {row.payment_status ? <StatusBadge status={row.payment_status} /> : '--'}
                                            </td>
                                            <td style={{ padding: '0.65rem 0.85rem', textAlign: 'right', fontWeight: 600 }}>
                                                {row.debit ? formatCurrency(row.debit) : ''}
                                            </td>
                                            <td style={{ padding: '0.65rem 0.85rem', textAlign: 'right', fontWeight: 600 }}>
                                                {row.credit ? formatCurrency(row.credit) : ''}
                                            </td>
                                            <td style={{ padding: '0.65rem 0.85rem', textAlign: 'right', fontWeight: 700 }}>
                                                {row.balance || (row.final_total ? formatCurrency(row.final_total) : '--')}
                                            </td>
                                            <td style={{ padding: '0.65rem 0.85rem' }}>{row.payment_method || row.method || '--'}</td>
                                            <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.78rem', color: '#64748b' }}>
                                                <span dangerouslySetInnerHTML={{ __html: row.others || '' }} />
                                            </td>
                                        </tr>

                                        {/* Inline Product Lines Sub-Table */}
                                        {hasProducts && (
                                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                                <td colSpan={10} style={{ padding: '0.5rem 1rem 0.85rem 2rem' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', background: '#ffffff', borderRadius: '4px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                                        <thead>
                                                            <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', color: '#334155', textAlign: 'left' }}>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}># Product</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>UPC / SKU</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'center' }}>Quantity</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'right' }}>Unit Price</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'right' }}>Discount</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'right' }}>Tax</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'right' }}>Price inc. tax</th>
                                                                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'right' }}>Subtotal</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {productLines.map((p, pIdx) => {
                                                                const pKey = p.variation_id || p.product_id;
                                                                const resolved = pKey ? resolvedProducts[pKey] : null;

                                                                const fullProdName = resolved?.name || p.name || p.product_name || p.product?.name || (p.product_id ? `Product #${p.product_id}` : '--');
                                                                const sku = resolved?.sku || p.sku || p.variations?.sub_sku || p.product?.sku || p.sub_sku || '--';
                                                                const qty = Number(p.quantity || p.qty || 1);
                                                                const unit = p.unit || p.sub_unit?.short_name || p.product?.unit?.short_name || p.product_unit || 'Box';
                                                                const unitPrice = Number(p.unit_price_before_discount || p.unit_price_before_tax || p.unitPrice || p.unit_price || p.purchase_price || 0);
                                                                const discount = Number(p.line_discount_amount || p.discount || 0);
                                                                const tax = Number(p.item_tax || p.tax || 0);
                                                                const priceIncTax = Number(p.unit_price_inc_tax || p.unitPrice || p.unit_price || p.purchase_price || 0);
                                                                const subtotal = Number(p.subtotal || (qty * (priceIncTax || unitPrice)));

                                                                return (
                                                                    <tr key={`f3-p-${p.id || pIdx}-${pIdx}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                        <td style={{ padding: '0.45rem 0.75rem', color: '#1e293b', fontWeight: 500 }}>
                                                                            {pIdx + 1} {fullProdName}
                                                                        </td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', color: '#64748b' }}>{sku}</td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'center' }}>{qty.toFixed(2)} {unit}</td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right' }}>{formatCurrency(unitPrice)}</td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', color: '#dc2626' }}>{formatCurrency(discount)}</td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', color: '#d97706' }}>{formatCurrency(tax)}</td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right' }}>{formatCurrency(priceIncTax)}</td>
                                                                        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{formatCurrency(subtotal)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ─── Shared TabTable ──────────────────────────────────────────────────────────
const TabTable = ({ loading, columns, rows, emptyMsg, rowRenderer }) => (
    <div style={{ overflowX: 'auto', background: 'var(--etpos-card)', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>
                <div style={{ marginBottom: '0.5rem', fontSize: '1.25rem' }}>⏳</div>
                <div style={{ fontWeight: 600 }}>Loading...</div>
            </div>
        ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                    <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid #e2e8f0' }}>
                        {columns.map(h => (
                            <th key={h} style={{ padding: '0.85rem 1rem', textAlign: 'left', color: 'var(--etpos-text)', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>
                                {emptyMsg || 'No data available'}
                            </td>
                        </tr>
                    ) : (
                        rows.map(row => rowRenderer(row))
                    )}
                </tbody>
            </table>
        )}
    </div>
);

// ─── Main LedgerModal ─────────────────────────────────────────────────────────
const LedgerModal = ({ sale, onClose }) => {
    const { formatCurrency } = useBusinessSettings();
    const navigate = useNavigate();
    const [bizName, setBizName] = useState(() => localStorage.getItem('etpos_business_name') || '');

    useEffect(() => {
        apiService.getBusinessDetails()
            .then(res => {
                const bData = res?.data?.business || res?.data || res || {};
                const name = bData.name || bData.business_name;
                if (name) {
                    setBizName(name);
                    localStorage.setItem('etpos_business_name', name);
                }
            })
            .catch(() => {});
    }, []);

    const [activeTab, setActiveTab] = useState('Ledger');
    const [ledgerFormat, setLedgerFormat] = useState('Format 1');
    const [dateFrom, setDateFrom] = useState('2026-01-01');
    const [dateTo, setDateTo] = useState('2026-12-31');
    const [location, setLocation] = useState('all');
    const [businessLocations, setBusinessLocations] = useState([]);

    // Dynamic Contact State
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);

    // View Payment Modal State
    const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
    const [selectedPaymentRow, setSelectedPaymentRow] = useState(null);

    // Add Payment Modal State
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

    // View Sale Modal State
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedSaleForView, setSelectedSaleForView] = useState(null);

    // Edit Shipping Modal State
    const [showEditShippingModal, setShowEditShippingModal] = useState(false);
    const [selectedSaleForShipping, setSelectedSaleForShipping] = useState(null);

    // Sell Return Modal State
    const [showSellReturnModal, setShowSellReturnModal] = useState(false);
    const [selectedSaleForReturn, setSelectedSaleForReturn] = useState(null);

    // Payment View/Edit Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [paymentModalMode, setPaymentModalMode] = useState('view');

    // Real Ledger Data State
    const [ledgerData, setLedgerData] = useState(null);
    const [isLoadingLedger, setIsLoadingLedger] = useState(false);

    // Tab data states
    const [salesData, setSalesData] = useState([]);
    const [purchasesData, setPurchasesData] = useState([]);
    const [stockReportData, setStockReportData] = useState([]);
    const [paymentsData, setPaymentsData] = useState([]);
    const [documentsData, setDocumentsData] = useState([]);
    const [rewardsData, setRewardsData] = useState([]);
    const [activitiesData, setActivitiesData] = useState([]);
    const [contactPersonsData, setContactPersonsData] = useState([]);

    const [tabLoading, setTabLoading] = useState({});

    // Sales tab filter & pagination state
    const [salesPaymentStatus, setSalesPaymentStatus] = useState('all');
    const [salesSearch, setSalesSearch] = useState('');
    const [salesPage, setSalesPage] = useState(1);
    const [salesPerPage, setSalesPerPage] = useState(25);
    const [salesTotal, setSalesTotal] = useState(0);
    const [salesOpenActionId, setSalesOpenActionId] = useState(null);
    const [selectedSaleRow, setSelectedSaleRow] = useState(null);

    // Purchases tab action dropdown state
    const [purchasesOpenActionId, setPurchasesOpenActionId] = useState(null);

    // Purchase view / update status modal states
    const [showViewPurchaseModal, setShowViewPurchaseModal] = useState(false);
    const [selectedPurchaseForView, setSelectedPurchaseForView] = useState(null);
    const [isLoadingViewPurchase, setIsLoadingViewPurchase] = useState(false);
    const [showUpdatePurchaseStatusModal, setShowUpdatePurchaseStatusModal] = useState(false);
    const [selectedPurchaseForStatus, setSelectedPurchaseForStatus] = useState(null);

    // Pay Contact Due modal state
    const [showPayContactDueModal, setShowPayContactDueModal] = useState(false);
    const [payContactDueLoading, setPayContactDueLoading] = useState(false);
    const [payContactDueAccounts, setPayContactDueAccounts] = useState([]);
    const [payContactDueMethods, setPayContactDueMethods] = useState([]);
    const [payContactDueData, setPayContactDueData] = useState({
        method: 'cash',
        paidOn: '',
        amount: '',
        accountId: '',
        note: '',
        cardNumber: '',
        cardHolderName: '',
        cardType: 'visa',
        cardMonth: '',
        cardYear: '',
        cardSecurity: '',
        cardTransactionNumber: '',
        chequeNumber: '',
        chequeExpiryDate: '',
        bankSubmissionDate: '',
        bankAccountNumber: ''
    });

    React.useEffect(() => {
        const fetchContacts = async () => {
            setIsLoadingContacts(true);
            try {
                const response = await apiService.getContacts();
                if (response && response.data) {
                    setContacts(response.data);

                    // Try to match selected contact with initial sale
                    let match = null;
                    if (sale?.contact_id) {
                        match = response.data.find(c => String(c.id) === String(sale.contact_id));
                    } else if (sale?.customer) {
                        match = response.data.find(c => c.name === sale.customer);
                    }

                    if (match) {
                        setSelectedContact(match);
                    } else if (sale?.contact_id) {
                        // Fallback: fetch specific contact by ID if not in list
                        try {
                            const contactResp = await apiService.getCustomerById(sale.contact_id);
                            const contactData = contactResp?.data?.[0] || contactResp?.data;
                            if (contactData) setSelectedContact(contactData);
                        } catch (e) {
                            /* console.error('Failed to fetch specific contact:', e); */
                        }
                    }
                }
            } catch (error) {
                /* console.error('Error fetching contacts for ledger:', error); */
            } finally {
                setIsLoadingContacts(false);
            }
        };
        fetchContacts();
    }, [sale]);

    const handleViewPayment = (row) => {
        setSelectedPaymentRow(row);
        setShowViewPaymentsModal(true);
    };

    const handlePayContactDueChange = (field, value) => {
        setPayContactDueData(prev => ({ ...prev, [field]: value }));
    };

    const handleOpenPayContactDue = async () => {
        if (!selectedContact?.id) return;
        const contactLoc = selectedContact?.location_id || (location !== 'all' ? location : null);
        try {
            const params = contactLoc ? { location_id: contactLoc, per_page: -1 } : { per_page: -1 };
            const resp = await apiService.getPaymentAccounts(params);
            const accounts = resp?.data || resp || [];
            setPayContactDueAccounts(Array.isArray(accounts) ? accounts : []);
        } catch (e) {
            setPayContactDueAccounts([]);
        }

        try {
            const methodsData = await apiService.getPaymentMethods(contactLoc);
            let list = [];
            if (typeof methodsData === 'object' && methodsData !== null && !Array.isArray(methodsData)) {
                // Ignore raw objects with unconfigured custom_pay placeholders
                list = Object.entries(methodsData)
                    .filter(([k, v]) => !k.startsWith('custom_pay') || (typeof v === 'string' && !v.toLowerCase().includes('custom payment')))
                    .map(([k, v]) => ({ id: k, name: typeof v === 'string' ? v : (v.name || k) }));
            } else if (Array.isArray(methodsData?.data)) {
                list = methodsData.data.map(m => ({ id: m.id || m.key || m.name, name: m.name || m.label || String(m.id) }));
            } else if (Array.isArray(methodsData)) {
                list = methodsData.map(m => ({ id: m.id || m.key || m.name, name: m.name || m.label || String(m.id) }));
            }

            if (list.length === 0) {
                list = [
                    { id: 'cash', name: 'Cash' },
                    { id: 'card', name: 'Card' },
                    { id: 'cheque', name: 'Check' },
                    { id: 'bank_transfer', name: 'Bank Transfer' },
                    { id: 'credit', name: 'Credit' },
                    { id: 'other', name: 'Other' }
                ];
            }
            setPayContactDueMethods(list);
        } catch (e) {
            setPayContactDueMethods([
                { id: 'cash', name: 'Cash' },
                { id: 'card', name: 'Card' },
                { id: 'cheque', name: 'Check' },
                { id: 'bank_transfer', name: 'Bank Transfer' },
                { id: 'credit', name: 'Credit' },
                { id: 'other', name: 'Other' }
            ]);
        }

        setPayContactDueData({
            method: 'cash',
            paidOn: dateUtils.getChicagoISOString(),
            amount: data.balanceDue > 0 ? Number(data.balanceDue).toFixed(2) : '',
            accountId: '',
            note: '',
            cardNumber: '',
            cardHolderName: '',
            cardType: 'visa',
            cardMonth: '',
            cardYear: '',
            cardSecurity: '',
            cardTransactionNumber: '',
            chequeNumber: '',
            chequeExpiryDate: '',
            bankSubmissionDate: '',
            bankAccountNumber: ''
        });
        setShowPayContactDueModal(true);
    };

    const handleSavePayContactDue = async () => {
        if (!selectedContact?.id) return;
        const amount = parseFloat(payContactDueData.amount);
        if (!amount || amount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (!payContactDueData.paidOn) {
            toast.error('Please select paid on date');
            return;
        }

        const payload = {
            contact_id: selectedContact.id,
            amount: amount,
            method: payContactDueData.method,
            paid_on: payContactDueData.paidOn,
            note: payContactDueData.note || '',
            account_id: payContactDueData.accountId || null,
            payment_for: selectedContact.type === 'supplier' ? 'purchase' : 'sell'
        };

        if (payContactDueData.method === 'card') {
            payload.card_number = payContactDueData.cardNumber;
            payload.card_holder_name = payContactDueData.cardHolderName;
            payload.card_type = payContactDueData.cardType;
            payload.card_month = payContactDueData.cardMonth;
            payload.card_year = payContactDueData.cardYear;
            payload.card_security = payContactDueData.cardSecurity;
            payload.card_transaction_number = payContactDueData.cardTransactionNumber;
        } else if (payContactDueData.method === 'cheque') {
            payload.cheque_number = payContactDueData.chequeNumber;
            payload.cheque_expiry = payContactDueData.chequeExpiryDate;
            payload.bank_submission_date = payContactDueData.bankSubmissionDate;
        } else if (payContactDueData.method === 'bank_transfer') {
            payload.bank_account_number = payContactDueData.bankAccountNumber;
        }

        const handleViewPayment = async (row) => {
            const txnId = row.transaction_id || row.id;
            const invoiceNo = row.transaction || row.ref_no || row.invoice_no;
            let matchingSale = salesData.find(s =>
                (txnId && String(s.id) === String(txnId)) ||
                (invoiceNo && (s.invoice_no === invoiceNo || s.invoiceNo === invoiceNo))
            );

            try {
                const targetId = matchingSale ? matchingSale.id : txnId;
                if (targetId) {
                    const resp = await apiService.getSale(targetId);
                    const fullSale = resp.data?.transaction || resp.data?.[0] || resp.data || resp || {};
                    setSelectedPaymentRow({ ...(matchingSale || fullSale), apiData: fullSale });
                    setShowViewPaymentsModal(true);
                } else {
                    toast.error('Transaction not found for this payment');
                }
            } catch (err) {
                toast.error('Failed to load transaction details');
            }
        };

        setPayContactDueLoading(true);
        try {
            await apiService.payContactDue(payload);
            toast.success('Payment saved successfully');
            setShowPayContactDueModal(false);
            // Refresh ledger data
            if (selectedContact?.id) {
                const formatMap = { 'Format 1': 'format_1', 'Format 2': 'format_2', 'Format 3': 'format_3' };
                const apiFormat = formatMap[ledgerFormat] || 'format_1';
                const response = await apiService.getContactLedger(
                    selectedContact.id,
                    dateFrom || null,
                    dateTo || null,
                    apiFormat,
                    location !== 'all' ? location : null
                );
                setLedgerData(response?.data);
            }
        } catch (err) {
            /* console.error('Pay contact due failed:', err); */
            toast.error(err.message || 'Failed to save payment');
        } finally {
            setPayContactDueLoading(false);
        }
    };

    // Fetch locations on mount
    React.useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await apiService.getBusinessLocations();
                const list = response?.data || response || [];
                setBusinessLocations(Array.isArray(list) ? list : []);
            } catch (err) {
                /* console.error('Error fetching business locations:', err); */
            }
        };
        fetchLocations();
    }, []);

    // Fetch real ledger data when contact or filters change
    React.useEffect(() => {
        const fetchLedger = async () => {
            if (!selectedContact?.id) return;
            setIsLoadingLedger(true);
            try {
                const formatMap = { 'Format 1': 'format_1', 'Format 2': 'format_2', 'Format 3': 'format_3' };
                const apiFormat = formatMap[ledgerFormat] || 'format_1';
                const response = await apiService.getContactLedger(
                    selectedContact.id,
                    dateFrom || null,
                    dateTo || null,
                    apiFormat,
                    location !== 'all' ? location : null
                );
                setLedgerData(response?.data);

                try {
                    const salesResp = await apiService.getSales({ contact_id: selectedContact.id, per_page: -1, status: 'final' });
                    const salesList = salesResp?.data || salesResp || [];
                    if (Array.isArray(salesList)) setSalesData(salesList);
                } catch (e) { }
            } catch (error) {
            } finally {
                setIsLoadingLedger(false);
            }
        };
        fetchLedger();
    }, [selectedContact, dateFrom, dateTo, ledgerFormat, location]);

    // Extract payments from ledger data for Payments tab
    React.useEffect(() => {
        if (!ledgerData?.ledger) return;
        const paymentEntries = ledgerData.ledger.filter(
            entry => entry.payment_id || String(entry.type).toLowerCase().includes('payment')
        );
        setPaymentsData(paymentEntries);
    }, [ledgerData]);

    // Close sales action dropdown on outside click
    React.useEffect(() => {
        if (!salesOpenActionId) return;
        const handleDocClick = (e) => {
            const btn = e.target.closest('[data-sales-action]');
            if (!btn) setSalesOpenActionId(null);
        };
        document.addEventListener('click', handleDocClick);
        return () => document.removeEventListener('click', handleDocClick);
    }, [salesOpenActionId]);

    // Fetch tab-specific data when active tab changes
    React.useEffect(() => {
        if (!selectedContact?.id) return;
        const contactId = selectedContact.id;
        const type = selectedContact.type;

        const fetchTabData = async () => {
            switch (activeTab) {
                case 'Sales': {
                    setTabLoading(prev => ({ ...prev, Sales: true }));
                    try {
                        const resp = await apiService.getSales({
                            contact_id: contactId,
                            payment_status: salesPaymentStatus,
                            start_date: dateFrom,
                            end_date: dateTo,
                            per_page: salesPerPage,
                            page: salesPage,
                            status: 'final'
                        });
                        const list = resp?.data || resp || [];
                        setSalesData(Array.isArray(list) ? list : []);
                        setSalesTotal(resp?.meta?.total || list.length || 0);
                    } catch (e) { /* console.error('Sales fetch error:', e); */ }
                    setTabLoading(prev => ({ ...prev, Sales: false }));
                    break;
                }
                case 'Purchases': {
                    setTabLoading(prev => ({ ...prev, Purchases: true }));
                    try {
                        const resp = await apiService.getPurchases({ supplier_id: contactId, per_page: 100 });
                        const list = resp?.data || resp || [];
                        setPurchasesData(Array.isArray(list) ? list : []);
                    } catch (e) { /* console.error('Purchases fetch error:', e); */ }
                    setTabLoading(prev => ({ ...prev, Purchases: false }));
                    break;
                }
                case 'Stock Report': {
                    setTabLoading(prev => ({ ...prev, 'Stock Report': true }));
                    try {
                        const resp = await apiService.getSupplierStockReport(contactId);
                        const list = resp?.data || resp || [];
                        setStockReportData(Array.isArray(list) ? list : []);
                    } catch (e) { /* console.error('Stock report fetch error:', e); */ }
                    setTabLoading(prev => ({ ...prev, 'Stock Report': false }));
                    break;
                }
                case "Lefty's Rewards":
                case rewardsTabTitle: {
                    setTabLoading(prev => ({ ...prev, Rewards: true }));
                    try {
                        const resp = await apiService.getSales({ contact_id: contactId, rewards_only: true, per_page: 100 });
                        const list = resp?.data || resp || [];
                        setRewardsData(Array.isArray(list) ? list : []);
                    } catch (e) { /* console.error('Rewards fetch error:', e); */ }
                    setTabLoading(prev => ({ ...prev, Rewards: false }));
                    break;
                }
                case 'Payments': {
                    setTabLoading(prev => ({ ...prev, Payments: true }));
                    try {
                        let paymentsList = [];
                        try {
                            const pResp = await apiService.getContactPayments(contactId);
                            paymentsList = pResp?.data || pResp || [];
                        } catch (e1) { }

                        if (!Array.isArray(paymentsList) || paymentsList.length === 0) {
                            if (ledgerData?.ledger) {
                                paymentsList = ledgerData.ledger.filter(
                                    entry => entry.payment_id || String(entry.type).toLowerCase().includes('payment')
                                );
                            }
                        }

                        // Also load sales data so transaction invoice_no is always resolved
                        const salesResp = await apiService.getSales({
                            contact_id: contactId,
                            per_page: -1,
                            status: 'final'
                        });
                        const salesList = salesResp?.data || salesResp || [];
                        if (Array.isArray(salesList)) setSalesData(salesList);

                        setPaymentsData(Array.isArray(paymentsList) ? paymentsList : []);
                    } catch (e) { }
                    setTabLoading(prev => ({ ...prev, Payments: false }));
                    break;
                }
                default:
                    break;
            }
        };

        fetchTabData();
    }, [activeTab, selectedContact, salesPaymentStatus, dateFrom, dateTo, salesPerPage, salesPage]);

    const data = ledgerData ? buildLedgerData(ledgerData, selectedContact) : generateDummyLedger(sale, selectedContact);

    const customerFallbackStr = (typeof data?.customer === 'object' ? data?.customer?.name : data?.customer) || (typeof sale?.customer === 'object' ? sale?.customer?.name : sale?.customer) || sale?.contact?.name || 'Customer';
    const contactName = selectedContact?.name || customerFallbackStr;
    const firstContactWord = (contactName || '').trim().split(/\s+/)[0]?.replace(/['’]s$/i, '');
    const rewardsTabTitle = firstContactWord ? `${firstContactWord}'s Rewards` : 'Rewards';

    const isSupplierType = selectedContact?.type === 'supplier' || selectedContact?.type === 'both';
    const isCustomerType = selectedContact?.type === 'customer' || selectedContact?.type === 'both';

    const tabsList = [
        'Ledger',
        ...(isCustomerType ? ['Sales'] : []),
        ...(isSupplierType ? ['Purchases', 'Stock Report'] : []),
        'Documents & Note',
        'Payments',
        ...(isCustomerType ? [rewardsTabTitle] : []),
        'Activities',
        'Contact Persons'
    ];

    return (
        <div className="modal-overlay ledger-modal-overlay">
            {/* Modal Body */}
            <div className="modal-container ledger-modal-container dark-mode-modal">

                {/* ── View Contact Header ── */}
                <div className="ledger-header">
                    <h2 className="ledger-title">View Contact</h2>
                    <div className="ledger-header-actions">
                        <div className="ledger-contact-select-wrapper">
                            <select
                                value={selectedContact?.id || ''}
                                onChange={(e) => {
                                    const contact = contacts.find(c => String(c.id) === e.target.value);
                                    setSelectedContact(contact);
                                }}
                                className="ledger-contact-select"
                            >
                                {isLoadingContacts ? (
                                    <option>Loading...</option>
                                ) : (
                                    contacts.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name} {c.contact_id ? `- (${c.contact_id})` : ''}
                                        </option>
                                    ))
                                )}
                                {contacts.length === 0 && !isLoadingContacts && (
                                    <option value="">{customerFallbackStr}</option>
                                )}
                            </select>
                        </div>
                        <button onClick={onClose} className="ledger-close-btn" aria-label="Close">
                            <X size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                {/* ── Contact Details Card ── */}
                <div className="ledger-contact-card">
                    <div className="ledger-contact-grid">

                        {/* Left Col */}
                        <div className="ledger-contact-left">
                            <div className="ledger-contact-profile">
                                <div className="ledger-profile-icon">
                                    <User size={28} color="#334155" />
                                </div>
                                <div className="ledger-profile-info">
                                    <h3 className="ledger-customer-name">{data.customer}</h3>
                                    <span className="ledger-customer-label">Customer</span>
                                </div>
                            </div>

                            <div className="ledger-contact-fields">
                                <div className="ledger-field-item">
                                    <MapPin className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Address</strong>
                                        <div className="ledger-field-value address-value">{data.address}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Smartphone className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Mobile</strong>
                                        <div className="ledger-field-value">{data.mobile}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Phone className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Landline</strong>
                                        <div className="ledger-field-value">{data.landline}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Phone className="ledger-field-icon alternate-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Alternate contact</strong>
                                        <div className="ledger-field-value">{data.alternateContact}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Calendar className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Date of birth</strong>
                                        <div className="ledger-field-value">{data.dob}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Col */}
                        <div className="ledger-contact-right">
                            <div className="ledger-custom-fields">
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 1</strong>
                                    <div className="ledger-field-value">{data.customField1}</div>
                                </div>
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 2</strong>
                                    <div className="ledger-field-value">{data.customField2}</div>
                                </div>
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 3</strong>
                                    <div className="ledger-field-value">{data.customField3}</div>
                                </div>
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 4</strong>
                                    <div className="ledger-field-value">{data.customField4}</div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="ledger-action-buttons">
                                {data.balanceDue > 0 ? (
                                    <button
                                        onClick={handleOpenPayContactDue}
                                        className="ledger-pay-btn"
                                    >
                                        <DollarSign size={16} /> Pay Due Amount
                                    </button>
                                ) : (
                                    <div className="ledger-paid-badge">
                                        <TrendingUp size={16} /> Paid
                                    </div>
                                )}
                                <button className="ledger-discount-btn">
                                    Add Discount
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Tabs Header ── */}
                <div className="ledger-tabs-header">
                    <div className="ledger-tabs-list">
                        {tabsList.map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`ledger-tab-btn ${activeTab === tab ? 'active' : ''}`}
                            >
                                {tab === 'Ledger' && <span className="tab-icon">📊</span>}
                                {tab === 'Sales' && <span className="tab-icon">📈</span>}
                                {tab === 'Purchases' && <span className="tab-icon">📥</span>}
                                {tab === 'Stock Report' && <span className="tab-icon">📦</span>}
                                {tab === 'Payments' && <span className="tab-icon">💳</span>}
                                {tab === 'Documents & Note' && <span className="tab-icon">📎</span>}
                                {(tab === rewardsTabTitle || tab.includes('Rewards')) && <span className="tab-icon">🎁</span>}
                                {tab === 'Activities' && <span className="tab-icon">📝</span>}
                                {tab === 'Contact Persons' && <span className="tab-icon">👥</span>}
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Content Area ── */}
                <div className="ledger-content-area">

                    {/* ── Sales Tab ── */}
                    {activeTab === 'Sales' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {/* Filters Row */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', background: 'var(--etpos-card)', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--etpos-text-muted)' }}>Payment Status:</label>
                                    <select value={salesPaymentStatus} onChange={e => { setSalesPaymentStatus(e.target.value); setSalesPage(1); }} style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', minWidth: '140px' }}>
                                        <option value="all">All</option>
                                        <option value="paid">Paid</option>
                                        <option value="due">Due</option>
                                        <option value="partial">Partial</option>
                                    </select>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--etpos-text-muted)' }}>Date Range:</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setSalesPage(1); }} style={{ padding: '0.4rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }} />
                                        <span style={{ color: 'var(--etpos-text-muted)' }}>to</span>
                                        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setSalesPage(1); }} style={{ padding: '0.4rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '1.1rem' }}>
                                    <input id="salesSub" type="checkbox" style={{ width: '16px', height: '16px' }} />
                                    <label htmlFor="salesSub" style={{ fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>Subscriptions</label>
                                </div>
                            </div>

                            {/* Toolbar Row */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#475569' }}>Show</span>
                                    <select value={salesPerPage} onChange={e => { setSalesPerPage(Number(e.target.value)); setSalesPage(1); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}>
                                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <span style={{ fontSize: '0.85rem', color: '#475569' }}>entries</span>
                                </div>
                                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                    {['Export CSV', 'Export Excel', 'Print', 'Column visibility', 'Export PDF'].map(label => (
                                        <button key={label} style={{ padding: '0.35rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem', background: 'var(--etpos-card)', color: '#475569', cursor: 'pointer', fontWeight: 500 }}>{label}</button>
                                    ))}
                                </div>
                                <input type="text" placeholder="Search..." value={salesSearch} onChange={e => setSalesSearch(e.target.value)} style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', minWidth: '180px' }} />
                            </div>

                            {/* Sales Table */}
                            <div style={{ overflowX: 'auto', background: 'var(--etpos-card)', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                {tabLoading['Sales'] ? (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>
                                        <div style={{ marginBottom: '0.5rem', fontSize: '1.25rem' }}>⏳</div>
                                        <div style={{ fontWeight: 600 }}>Loading...</div>
                                    </div>
                                ) : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid #e2e8f0' }}>
                                                {['Action', 'Date', 'Invoice No.', 'Customer name', 'Contact Number', 'Location', 'Payment Status', 'Payment Method', 'Total amount', 'Total paid', 'Sell Due', 'Sell Return Due', 'Shipping Status', 'Total Items', 'Added By', 'Sell note'].map(h => (
                                                    <th key={h} style={{ padding: '0.85rem 1rem', textAlign: 'left', color: 'var(--etpos-text)', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const filtered = salesData.filter(row => {
                                                    if (!salesSearch) return true;
                                                    const term = salesSearch.toLowerCase();
                                                    return (row.invoice_no || '').toLowerCase().includes(term) ||
                                                        (row.customer_name || row.customer || '').toLowerCase().includes(term) ||
                                                        (row.contact_number || '').toLowerCase().includes(term) ||
                                                        (row.location_name || '').toLowerCase().includes(term) ||
                                                        (row.payment_status || '').toLowerCase().includes(term);
                                                });
                                                const start = (salesPage - 1) * salesPerPage;
                                                const paginated = filtered.slice(start, start + salesPerPage);
                                                const totals = filtered.reduce((acc, row) => {
                                                    const finalTotal = parseFloat(row.final_total || 0);
                                                    const paid = row.payment_lines?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || parseFloat(row.total_paid || 0);
                                                    const due = Math.max(0, finalTotal - paid);
                                                    const returnDue = parseFloat(row.sell_return_due || (row.return_transaction ? row.return_transaction.amount_due : 0) || 0);
                                                    return {
                                                        totalAmount: acc.totalAmount + finalTotal,
                                                        totalPaid: acc.totalPaid + paid,
                                                        totalDue: acc.totalDue + due,
                                                        totalReturnDue: acc.totalReturnDue + returnDue
                                                    };
                                                }, { totalAmount: 0, totalPaid: 0, totalDue: 0, totalReturnDue: 0 });
                                                return (
                                                    <>
                                                        {paginated.length === 0 ? (
                                                            <tr><td colSpan={16} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                                                        ) : (
                                                            paginated.map(row => {
                                                                const finalTotal = parseFloat(row.final_total || 0);
                                                                const paid = row.payment_lines?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || parseFloat(row.total_paid || 0);
                                                                const due = Math.max(0, finalTotal - paid);
                                                                const returnDue = parseFloat(row.sell_return_due || (row.return_transaction ? row.return_transaction.amount_due : 0) || 0);
                                                                return (
                                                                    <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                        <td style={{ padding: '0.65rem 1rem', position: 'relative' }}>
                                                                            <button
                                                                                data-sales-action
                                                                                onClick={() => setSalesOpenActionId(salesOpenActionId === row.id ? null : row.id)}
                                                                                style={{
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    gap: '0.35rem',
                                                                                    padding: '0.35rem 0.85rem',
                                                                                    background: '#3b82f6',
                                                                                    color: 'var(--etpos-card)',
                                                                                    border: 'none',
                                                                                    borderRadius: '10px',
                                                                                    fontSize: '0.78rem',
                                                                                    fontWeight: 600,
                                                                                    cursor: 'pointer',
                                                                                    minWidth: '70px'
                                                                                }}
                                                                            >
                                                                                <span>Actions</span>
                                                                                <span style={{
                                                                                    width: 0,
                                                                                    height: 0,
                                                                                    borderLeft: '4px solid transparent',
                                                                                    borderRight: '4px solid transparent',
                                                                                    borderTop: '4px solid #fff',
                                                                                    opacity: 0.9
                                                                                }} />
                                                                            </button>
                                                                            {salesOpenActionId === row.id && (
                                                                                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, background: 'var(--etpos-card)', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '160px', marginTop: '4px' }}>
                                                                                    {[
                                                                                        { label: 'View', icon: '👁' },
                                                                                        { label: 'Edit', icon: '✎' },
                                                                                        { label: 'Delete', icon: '🗑' },
                                                                                        { label: 'Edit Shipping', icon: '🚚' },
                                                                                        { label: 'Print Invoice', icon: '🖨' },
                                                                                        { label: 'View Payments', icon: '💳' },
                                                                                        ...(String(row.payment_status).toLowerCase() === 'paid' ? [] : [{ label: 'Add Payment', icon: '💵' }]),
                                                                                        { label: 'Sell Return', icon: '↩' },
                                                                                        { label: 'Invoice URL', icon: '🔗' }
                                                                                    ].map(action => (
                                                                                        <button
                                                                                            key={action.label}
                                                                                            onClick={async () => {
                                                                                                setSalesOpenActionId(null);
                                                                                                const apiData = row.apiData || row;
                                                                                                const invoiceUrl = apiData.invoice_url || row.invoiceUrl;
                                                                                                if (action.label === 'View') {
                                                                                                    try {
                                                                                                        const resp = await apiService.getSale(row.id);
                                                                                                        const saleData = resp.data?.[0] || resp.data || resp || {};
                                                                                                        setSelectedSaleForView({ ...row, apiData: saleData });
                                                                                                        setShowViewModal(true);
                                                                                                    } catch (err) {
                                                                                                        /* console.error('Error fetching sale details:', err); */
                                                                                                        toast.error('Failed to load sale details');
                                                                                                    }
                                                                                                } else if (action.label === 'Edit') {
                                                                                                    onClose();
                                                                                                    if (apiData.sub_status === 'quotation') {
                                                                                                        navigate('/sell/add-quotation', { state: { editSale: row } });
                                                                                                    } else if (apiData.status === 'draft' && apiData.sub_status !== 'proforma') {
                                                                                                        navigate('/sell/add-draft', { state: { editSale: row } });
                                                                                                    } else {
                                                                                                        navigate('/sell/add', { state: { editSale: row } });
                                                                                                    }
                                                                                                } else if (action.label === 'Delete') {
                                                                                                    if (window.confirm('Are you sure you want to delete this sale?')) {
                                                                                                        apiService.deleteSale(row.id).then(() => {
                                                                                                            setSalesData(prev => prev.filter(s => s.id !== row.id));
                                                                                                        }).catch(err => {
                                                                                                            /* console.error('Delete failed:', err); */
                                                                                                            toast.error('Failed to delete sale');
                                                                                                        });
                                                                                                    }
                                                                                                } else if (action.label === 'Edit Shipping') {
                                                                                                    try {
                                                                                                        const resp = await apiService.getSale(row.id);
                                                                                                        const saleData = resp.data?.[0] || resp.data || resp || {};
                                                                                                        setSelectedSaleForShipping({ ...row, apiData: saleData });
                                                                                                        setShowEditShippingModal(true);
                                                                                                    } catch (err) {
                                                                                                        /* console.error('Error fetching sale details:', err); */
                                                                                                        toast.error('Failed to load sale details');
                                                                                                    }
                                                                                                } else if (action.label === 'Print Invoice') {
                                                                                                    if (invoiceUrl) window.open(invoiceUrl, '_blank');
                                                                                                    else toast.error('Invoice URL not available');
                                                                                                } else if (action.label === 'View Payments') {
                                                                                                    setSelectedSaleRow(row);
                                                                                                    setSelectedPaymentRow(row);
                                                                                                    setShowViewPaymentsModal(true);
                                                                                                } else if (action.label === 'Add Payment') {
                                                                                                    try {
                                                                                                        const resp = await apiService.getSale(row.id);
                                                                                                        const saleData = resp.data?.[0] || resp.data || resp || {};
                                                                                                        setSelectedSaleRow({ ...row, apiData: saleData });
                                                                                                        setShowAddPaymentModal(true);
                                                                                                    } catch (err) {
                                                                                                        /* console.error('Error fetching sale details:', err); */
                                                                                                        toast.error('Failed to load sale details');
                                                                                                    }
                                                                                                } else if (action.label === 'Sell Return') {
                                                                                                    try {
                                                                                                        const resp = await apiService.getSale(row.id);
                                                                                                        const saleData = resp.data?.[0] || resp.data || resp || {};
                                                                                                        setSelectedSaleForReturn({ ...row, apiData: saleData });
                                                                                                        setShowSellReturnModal(true);
                                                                                                    } catch (err) {
                                                                                                        /* console.error('Error fetching sale details:', err); */
                                                                                                        toast.error('Failed to load sale details');
                                                                                                    }
                                                                                                } else if (action.label === 'Invoice URL') {
                                                                                                    if (invoiceUrl) window.open(invoiceUrl, '_blank');
                                                                                                    else toast.error('Invoice URL not available');
                                                                                                }
                                                                                            }}
                                                                                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 0.75rem', border: 'none', background: 'none', fontSize: '0.85rem', color: 'var(--etpos-text)', cursor: 'pointer', textAlign: 'left' }}
                                                                                        >
                                                                                            <span>{action.icon}</span> {action.label}
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap' }}>{row.transaction_date || row.created_at || '--'}</td>
                                                                        <td style={{ padding: '0.65rem 1rem', color: '#6366f1', fontWeight: 600 }}>{row.invoice_no || '--'}</td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                                            {row.contact?.name || row.customer_name || (typeof row.customer === 'object' ? row.customer?.name : row.customer) || selectedContact?.name || '--'}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                                            {row.contact?.mobile || row.contact_number || selectedContact?.mobile || '--'}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                                            {row.location?.name || row.location_name || (typeof row.location === 'string' ? row.location : '--')}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}><StatusBadge status={row.payment_status} /></td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                                            {(() => {
                                                                                const methods = row.payment_lines ? Array.from(new Set(row.payment_lines.map(p => p.method).filter(Boolean))) : [];
                                                                                if (methods.length === 1) {
                                                                                    const m = methods[0];
                                                                                    return (m.toLowerCase() === 'cheque' || m.toLowerCase() === 'check') ? 'Check' : m.charAt(0).toUpperCase() + m.slice(1);
                                                                                } else if (methods.length > 1) {
                                                                                    return 'Multiple Pay';
                                                                                }
                                                                                return row.payment_method || '--';
                                                                            })()}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>{formatCurrency(finalTotal)}</td>
                                                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>{formatCurrency(paid)}</td>
                                                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>{formatCurrency(due)}</td>
                                                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>{formatCurrency(returnDue)}</td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                                            {row.shipping_status ? (
                                                                                <span style={{
                                                                                    padding: '0.25rem 0.65rem',
                                                                                    borderRadius: '6px',
                                                                                    fontSize: '0.75rem',
                                                                                    fontWeight: 700,
                                                                                    background: '#ffedd5',
                                                                                    color: '#c2410c',
                                                                                    textTransform: 'capitalize'
                                                                                }}>
                                                                                    {row.shipping_status}
                                                                                </span>
                                                                            ) : '--'}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'center' }}>
                                                                            {row.sell_lines?.length || row.total_items || 1}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                                            {row.service_staff?.user_full_name || row.created_by_user?.first_name ? `${row.created_by_user?.first_name || ''} ${row.created_by_user?.last_name || ''}`.trim() : (row.added_by || 'Mr Ajay Gogia')}
                                                                        </td>
                                                                        <td style={{ padding: '0.65rem 1rem' }}>{row.additional_notes || row.sell_note || '--'}</td>
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                        {/* Total Row */}
                                                        <tr style={{ background: 'var(--etpos-bg)', borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                                                            <td colSpan={8} style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>Total:</td>
                                                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{formatCurrency(totals.totalAmount)}</td>
                                                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{formatCurrency(totals.totalPaid)}</td>
                                                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{formatCurrency(totals.totalDue)}</td>
                                                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{formatCurrency(totals.totalReturnDue)}</td>
                                                            <td colSpan={4}></td>
                                                        </tr>
                                                    </>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Pagination */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>
                                    Showing {Math.min((salesPage - 1) * salesPerPage + 1, salesData.length)} to {Math.min(salesPage * salesPerPage, salesData.length)} of {salesData.length} entries
                                </span>
                                <div style={{ display: 'flex', gap: '0.3rem' }}>
                                    <button onClick={() => setSalesPage(p => Math.max(1, p - 1))} disabled={salesPage === 1} style={{ padding: '0.35rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: 'var(--etpos-card)', fontSize: '0.85rem', cursor: salesPage === 1 ? 'not-allowed' : 'pointer', opacity: salesPage === 1 ? 0.5 : 1 }}>Previous</button>
                                    <span style={{ padding: '0.35rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: 'var(--etpos-primary-light, #1e3a8a)', color: '#2563eb', fontWeight: 700, fontSize: '0.85rem' }}>{salesPage}</span>
                                    <button onClick={() => setSalesPage(p => p + 1)} disabled={salesPage * salesPerPage >= salesData.length} style={{ padding: '0.35rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: 'var(--etpos-card)', fontSize: '0.85rem', cursor: salesPage * salesPerPage >= salesData.length ? 'not-allowed' : 'pointer', opacity: salesPage * salesPerPage >= salesData.length ? 0.5 : 1 }}>Next</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Purchases Tab ── */}
                    {activeTab === 'Purchases' && (
                        <TabTable
                            loading={tabLoading['Purchases']}
                            columns={['Action', 'Date', 'Reference No', 'Location', 'Supplier', 'Purchase Status', 'Payment Status', 'Grand Total', 'Payment Due', 'Added By']}
                            rows={purchasesData}
                            rowRenderer={(row) => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.65rem 1rem', position: 'relative' }}>
                                        <button
                                            onClick={() => setPurchasesOpenActionId(purchasesOpenActionId === row.id ? null : row.id)}
                                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', padding: '0.35rem 0.85rem', background: '#3b82f6', color: 'var(--etpos-card)', border: 'none', borderRadius: '10px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', minWidth: '70px' }}
                                        >
                                            <span>Actions</span>
                                            <span style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid #fff', opacity: 0.9 }} />
                                        </button>
                                        {purchasesOpenActionId === row.id && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, background: 'var(--etpos-card)', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '160px', marginTop: '4px' }}>
                                                {[
                                                    { label: 'View', icon: '👁' },
                                                    { label: 'Edit', icon: '✎' },
                                                    { label: 'Delete', icon: '🗑' },
                                                    { label: 'Print', icon: '🖨' },
                                                    { label: 'View Payments', icon: '💳' },
                                                    ...(String(row.payment_status).toLowerCase() === 'paid' ? [] : [{ label: 'Add Payment', icon: '💵' }]),
                                                    { label: 'Update Status', icon: '✓' }
                                                ].map(action => (
                                                    <button
                                                        key={action.label}
                                                        onClick={async () => {
                                                            setPurchasesOpenActionId(null);
                                                            const apiData = row.apiData || row;
                                                            const printUrl = apiData.purchase_url || apiData.print_url || apiData.invoice_url;
                                                            if (action.label === 'View') {
                                                                setIsLoadingViewPurchase(true);
                                                                setSelectedPurchaseForView(row);
                                                                setShowViewPurchaseModal(true);
                                                                try {
                                                                    const resp = await apiService.getPurchase(row.id);
                                                                    const purchaseData = resp.data?.[0] || resp.data || resp || {};
                                                                    setSelectedPurchaseForView(purchaseData);
                                                                } catch (err) {
                                                                    /* console.error('Error fetching purchase details:', err); */
                                                                    toast.error('Failed to load purchase details');
                                                                } finally {
                                                                    setIsLoadingViewPurchase(false);
                                                                }
                                                            } else if (action.label === 'Edit') {
                                                                toast.info('Edit purchase coming soon');
                                                            } else if (action.label === 'Delete') {
                                                                if (window.confirm('Are you sure you want to delete this purchase?')) {
                                                                    apiService.deletePurchase(row.id).then(() => {
                                                                        setPurchasesData(prev => prev.filter(p => p.id !== row.id));
                                                                        toast.success('Purchase deleted successfully');
                                                                    }).catch(err => {
                                                                        /* console.error('Delete purchase failed:', err); */
                                                                        toast.error('Failed to delete purchase');
                                                                    });
                                                                }
                                                            } else if (action.label === 'Print') {
                                                                const buildPurchaseInvoiceData = (purchase) => ({
                                                                    invoiceNumber: purchase.invoice_no || purchase.ref_no || '--',
                                                                    invoiceDate: purchase.transaction_date ? new Date(purchase.transaction_date).toLocaleDateString() : '--',
                                                                    customerName: purchase.contact?.name || purchase.supplier || '--',
                                                                    items: (purchase.purchase_lines || []).map(line => ({
                                                                        sku: line.product?.sku || line.sub_sku || '',
                                                                        name: line.product?.name || ('Product #' + line.product_id),
                                                                        quantity: parseFloat(line.quantity || 0),
                                                                        unitPrice: parseFloat(line.purchase_price || 0),
                                                                        total: parseFloat(line.quantity || 0) * parseFloat(line.purchase_price || 0),
                                                                    })),
                                                                    payments: (purchase.payment_lines || []).map(p => ({
                                                                        method: p.method,
                                                                        amount: parseFloat(p.amount || 0),
                                                                        date: p.paid_on && !p.paid_on.startsWith('0000') ? new Date(p.paid_on).toLocaleDateString() : '--',
                                                                    })),
                                                                    subtotal: parseFloat(purchase.total_before_tax || purchase.final_total || 0),
                                                                    discount: parseFloat(purchase.discount_amount || 0),
                                                                    tax: parseFloat(purchase.tax_amount || 0),
                                                                    total: parseFloat(purchase.final_total || 0),
                                                                    totalPaid: (purchase.payment_lines || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0),
                                                                    amountDue: parseFloat(purchase.payment_due || 0),
                                                                    notes: purchase.additional_notes || '',
                                                                });
                                                                const apiData = row.apiData || row;
                                                                invoiceGenerator.printInvoicePDF(buildPurchaseInvoiceData(apiData));
                                                            } else if (action.label === 'View Payments') {
                                                                setSelectedSaleRow(row);
                                                                setSelectedPaymentRow(row);
                                                                setShowViewPaymentsModal(true);
                                                            } else if (action.label === 'Add Payment') {
                                                                setSelectedSaleRow(row);
                                                                setShowAddPaymentModal(true);
                                                            } else if (action.label === 'Update Status') {
                                                                setSelectedPurchaseForStatus(row);
                                                                setShowUpdatePurchaseStatusModal(true);
                                                            }
                                                        }}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 0.75rem', border: 'none', background: 'none', fontSize: '0.85rem', color: 'var(--etpos-text)', cursor: 'pointer', textAlign: 'left' }}
                                                    >
                                                        <span>{action.icon}</span> {action.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap' }}>{row.transaction_date || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem', color: '#6366f1', fontWeight: 600 }}>{row.ref_no || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.location_name || (row.location && typeof row.location === 'object' ? row.location.name : row.location) || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.supplier_name || (row.supplier && typeof row.supplier === 'object' ? row.supplier.name : row.supplier) || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}><StatusBadge status={row.status} /></td>
                                    <td style={{ padding: '0.65rem 1rem' }}><StatusBadge status={row.payment_status} /></td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>${Number(row.final_total || 0).toFixed(2)}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>${Number(row.payment_due || 0).toFixed(2)}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.added_by || '--'}</td>
                                </tr>
                            )}
                        />
                    )}

                    {/* ── Stock Report Tab ── */}
                    {activeTab === 'Stock Report' && (
                        <TabTable
                            loading={tabLoading['Stock Report']}
                            columns={['Product Name', 'SKU', 'Purchase Qty', 'Total Sold', 'Total Transfered', 'Total Returned', 'Current Stock', 'Stock Price']}
                            rows={stockReportData}
                            rowRenderer={(row) => (
                                <tr key={row.id || row.product_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.65rem 1rem', fontWeight: 600 }}>{row.product_name || row.name || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem', color: 'var(--etpos-text-muted)' }}>{row.sku || row.sub_sku || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'center' }}>{row.purchase_quantity || 0}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'center' }}>{row.total_quantity_sold || 0}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'center' }}>{row.total_quantity_transfered || 0}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'center' }}>{row.total_quantity_returned || 0}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{row.current_stock || 0}</td>
                                    <td style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>${Number(row.stock_price || 0).toFixed(2)}</td>
                                </tr>
                            )}
                        />
                    )}

                    {/* ── Documents & Note Tab ── */}
                    {activeTab === 'Documents & Note' && (
                        <TabTable
                            loading={false}
                            columns={['Action', 'Heading', 'Added By', 'Created At', 'Updated At']}
                            rows={documentsData}
                            emptyMsg="No data available in table"
                            rowRenderer={(row) => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.65rem 1rem' }}><button style={{ padding: '0.3rem 0.75rem', background: '#dbeafe', color: '#2563eb', border: 'none', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Actions ▼</button></td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.heading || row.title || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.added_by || row.user?.name || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.created_at ? new Date(row.created_at).toLocaleString() : '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '--'}</td>
                                </tr>
                            )}
                        />
                    )}

                    {/* ── Payments Tab ── */}
                    {activeTab === 'Payments' && (
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid #e2e8f0', color: 'var(--etpos-text-muted, #64748b)', textAlign: 'left' }}>
                                        <th style={{ padding: '0.65rem 1rem' }}>Paid on</th>
                                        <th style={{ padding: '0.65rem 1rem' }}>Reference No</th>
                                        <th style={{ padding: '0.65rem 1rem', textAlign: 'right' }}>Amount</th>
                                        <th style={{ padding: '0.65rem 1rem' }}>Payment Method</th>
                                        <th style={{ padding: '0.65rem 1rem' }}>Payment For</th>
                                        <th style={{ padding: '0.65rem 1rem' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentsData.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        paymentsData.map((row) => {
                                            const childPayments = row.child_payments || [];
                                            const hasChildren = childPayments.length > 0;

                                            const renderSingleRow = (p, isChild = false, parentRef = null) => {
                                                const methodStr = p.payment_method || p.method || '--';
                                                const isCheck = methodStr.toLowerCase() === 'cheque' || methodStr.toLowerCase() === 'check';
                                                const displayMethod = isCheck ? 'Check' : (methodStr !== '--' ? methodStr.charAt(0).toUpperCase() + methodStr.slice(1) : '--');
                                                const chkNo = p.cheque_number || p.check_number || p.checkNo || row.cheque_number;

                                                const txnObj = p.transaction || p.child_payments?.[0]?.transaction;
                                                const txnId = p.transaction_id || p.sale_id || txnObj?.id;
                                                const matchingSale = salesData.find(s =>
                                                    (txnId && String(s.id) === String(txnId)) ||
                                                    (p.invoice_no && String(s.invoice_no) === String(p.invoice_no))
                                                );
                                                const matchingPurchase = purchasesData.find(pur =>
                                                    (txnId && String(pur.id) === String(txnId)) ||
                                                    (p.ref_no && String(pur.ref_no) === String(p.ref_no))
                                                );

                                                const invNo = txnObj?.invoice_no || txnObj?.ref_no ||
                                                    p.transaction_invoice_no || p.invoice_no ||
                                                    matchingSale?.invoice_no || matchingSale?.invoiceNo ||
                                                    matchingPurchase?.ref_no || matchingPurchase?.refNo;

                                                const rawTxnType = txnObj?.type || p.transaction_type || p.payment_for || (matchingPurchase ? 'purchase' : 'sell');
                                                const typeLabel = String(rawTxnType).toLowerCase().includes('purchase') ? '(Purchase)' : '(Sell)';

                                                const pid = p.payment_id || p.id;
                                                const isBounced = p.is_bounced == 1 || p.is_bounced === '1' || row.is_bounced == 1;

                                                return (
                                                    <tr key={pid || Math.random()} style={{ borderBottom: '1px solid #f1f5f9', background: isChild ? '#f8fafc' : (hasChildren ? '#f1f5f9' : 'transparent') }}>
                                                        {!isChild && (
                                                            <td
                                                                rowSpan={hasChildren ? childPayments.length + 1 : 1}
                                                                style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap', verticalAlign: 'middle', background: '#ffffff', borderRight: '1px solid #f1f5f9' }}
                                                            >
                                                                {p.paid_on || p.date || row.paid_on || row.date || '--'}
                                                            </td>
                                                        )}
                                                        <td style={{ padding: '0.65rem 1rem', color: '#6366f1', fontWeight: 600 }}>
                                                            <div>{p.payment_ref_no || p.ref_no || '--'}</div>
                                                            {parentRef && (
                                                                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>
                                                                    Parent Payment: {parentRef}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'right', fontWeight: 700 }}>
                                                            ${Number(p.amount || p.debit || p.credit || 0).toFixed(2)}
                                                        </td>
                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                            <div>{displayMethod}</div>
                                                            {isCheck && (
                                                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                                    (Cheque No.: {chkNo || ''})
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                            {invNo ? (
                                                                <div>
                                                                    <div style={{ color: '#6366f1', fontWeight: 600 }}>{invNo}</div>
                                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{typeLabel}</div>
                                                                </div>
                                                            ) : (
                                                                <span>--</span>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '0.65rem 1rem' }}>
                                                            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                                                <button
                                                                    onClick={async () => {
                                                                        const targetTxnId = p.transaction_id || p.sale_id || txnId;
                                                                        let matchS = salesData.find(s =>
                                                                            (targetTxnId && String(s.id) === String(targetTxnId)) ||
                                                                            (invNo && (s.invoice_no === invNo || s.invoiceNo === invNo))
                                                                        );
                                                                        try {
                                                                            const targetId = matchS ? matchS.id : targetTxnId;
                                                                            if (targetId) {
                                                                                const resp = await apiService.getSale(targetId);
                                                                                const fullSale = resp.data?.transaction || resp.data?.[0] || resp.data || resp || {};
                                                                                setSelectedPaymentRow({ ...(matchS || fullSale), apiData: fullSale });
                                                                                setShowViewPaymentsModal(true);
                                                                            } else {
                                                                                toast.error('Transaction not found for this payment');
                                                                            }
                                                                        } catch (err) {
                                                                            toast.error('Failed to load transaction details');
                                                                        }
                                                                    }}
                                                                    style={{ padding: '0.25rem 0.5rem', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}
                                                                >👁 View</button>

                                                                {(isChild || !hasChildren) && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedPayment(p);
                                                                            setPaymentModalMode('edit');
                                                                            setShowPaymentModal(true);
                                                                        }}
                                                                        style={{ padding: '0.25rem 0.5rem', background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}
                                                                    >✎ Edit</button>
                                                                )}

                                                                <button
                                                                    onClick={async () => {
                                                                        if (!pid) { toast.error('Payment ID not found'); return; }
                                                                        if (!window.confirm('Are you sure you want to delete this payment?')) return;
                                                                        try {
                                                                            await apiService.deleteSellPayment(pid);
                                                                            setPaymentsData(prev => prev.filter(item => (item.payment_id || item.id) !== pid));
                                                                            toast.success('Payment deleted successfully');
                                                                        } catch (err) {
                                                                            toast.error('Failed to delete payment');
                                                                        }
                                                                    }}
                                                                    style={{ padding: '0.25rem 0.5rem', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}
                                                                >🗑 Delete</button>

                                                                {(!isChild && isCheck) && (
                                                                    isBounced ? (
                                                                        <button
                                                                            onClick={async () => {
                                                                                if (!pid) { toast.error('Payment ID not found'); return; }
                                                                                if (!window.confirm('Are you sure you want to revert check return?')) return;
                                                                                try {
                                                                                    const res = await apiService.revertCheckReturn(pid, p.transaction_id);
                                                                                    toast.success(res.msg || res.message || 'Check return reverted successfully');
                                                                                    setPaymentsData(prev => prev.map(item => (item.payment_id || item.id) === pid ? { ...item, is_bounced: 0 } : item));
                                                                                } catch (err) {
                                                                                    toast.error(err.message || 'Failed to revert check return');
                                                                                }
                                                                            }}
                                                                            style={{ padding: '0.25rem 0.5rem', background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
                                                                        >↩ Revert</button>
                                                                    ) : (
                                                                        <button
                                                                            onClick={async () => {
                                                                                if (!pid) { toast.error('Payment ID not found'); return; }
                                                                                if (!window.confirm('Are you sure you want to apply check return charges?')) return;
                                                                                try {
                                                                                    const res = await apiService.applyCheckReturn(pid, p.transaction_id);
                                                                                    toast.success(res.msg || res.message || 'Check return charges applied successfully');
                                                                                    setPaymentsData(prev => prev.map(item => (item.payment_id || item.id) === pid ? { ...item, is_bounced: 1 } : item));
                                                                                } catch (err) {
                                                                                    toast.error(err.message || 'Failed to apply check return');
                                                                                }
                                                                            }}
                                                                            style={{ padding: '0.25rem 0.5rem', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
                                                                        >Check Return</button>
                                                                    )
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            };

                                            return (
                                                <React.Fragment key={row.id || row.payment_id || Math.random()}>
                                                    {renderSingleRow(row, false, null)}
                                                    {hasChildren && childPayments.map(cp => renderSingleRow(cp, true, row.payment_ref_no || row.ref_no))}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Dynamic Rewards Tab ── */}
                    {(activeTab === rewardsTabTitle || activeTab.includes('Rewards')) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                                padding: '1rem 1.25rem',
                                borderRadius: '12px',
                                border: '1px solid #bfdbfe',
                                color: '#1e3a8a',
                                fontWeight: '700',
                                boxShadow: '0 2px 8px rgba(30, 58, 138, 0.05)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                                    <span style={{ fontSize: '1.35rem' }}>🎁</span>
                                    <span style={{ fontSize: '0.95rem' }}>Total Reward Points Balance:</span>
                                </div>
                                <span style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1d4ed8' }}>
                                    {selectedContact?.total_rp || 0}
                                </span>
                            </div>
                            <TabTable
                                loading={tabLoading['Rewards']}
                                columns={['Date', 'Invoice No.', 'Earned', 'Redeemed']}
                                rows={rewardsData}
                                rowRenderer={(row) => (
                                    <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap' }}>{row.transaction_date || row.saleDate || '--'}</td>
                                        <td style={{ padding: '0.65rem 1rem', color: '#6366f1', fontWeight: 600 }}>{row.invoice_no || row.invoiceNo || '--'}</td>
                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'center', color: '#16a34a', fontWeight: 700 }}>{row.rp_earned || 0}</td>
                                        <td style={{ padding: '0.65rem 1rem', textAlign: 'center', color: '#dc2626', fontWeight: 700 }}>{row.rp_redeemed || 0}</td>
                                    </tr>
                                )}
                            />
                        </div>
                    )}

                    {/* ── Activities Tab ── */}
                    {activeTab === 'Activities' && (
                        <TabTable
                            loading={false}
                            columns={['Date', 'Action', 'By', 'Note']}
                            rows={activitiesData}
                            emptyMsg="No records found"
                            rowRenderer={(row) => (
                                <tr key={row.id || Math.random()} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap' }}>{row.created_at ? new Date(row.created_at).toLocaleString() : row.date || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.description || row.action || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.causer?.name || row.by || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.note || row.properties || '--'}</td>
                                </tr>
                            )}
                        />
                    )}

                    {/* ── Contact Persons Tab ── */}
                    {activeTab === 'Contact Persons' && (
                        <TabTable
                            loading={false}
                            columns={['Action', 'Username', 'Name', 'Email', 'Department', 'Designation']}
                            rows={contactPersonsData}
                            emptyMsg="No data available in table"
                            rowRenderer={(row) => (
                                <tr key={row.id || Math.random()} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.65rem 1rem' }}><button style={{ padding: '0.3rem 0.75rem', background: '#dbeafe', color: '#2563eb', border: 'none', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Actions ▼</button></td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.username || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.name || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.email || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.department || '--'}</td>
                                    <td style={{ padding: '0.65rem 1rem' }}>{row.designation || '--'}</td>
                                </tr>
                            )}
                        />
                    )}

                    {/* Ledger Tab Content */}
                    {activeTab === 'Ledger' && (
                        <div className="ledger-main-content">

                            {/* Controls Row (Spa-styled) */}
                            <div className="ledger-controls-row">
                                {/* Date Range */}
                                <div className="ledger-control-group date-range-group">
                                    <label className="control-label">Date Range</label>
                                    <div className="date-input-wrapper">
                                        <Calendar className="date-icon" size={16} />
                                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="date-input" />
                                        <span className="date-separator">-</span>
                                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="date-input" />
                                    </div>
                                </div>

                                {/* Format Selector - React Pill Segment Control */}
                                <div className="ledger-control-group format-selector-group">
                                    <label className="control-label">Ledger Format</label>
                                    <div className="format-pills">
                                        {['Format 1', 'Format 2', 'Format 3'].map(fmt => (
                                            <button
                                                key={fmt}
                                                onClick={() => setLedgerFormat(fmt)}
                                                className={`format-pill-btn ${ledgerFormat === fmt ? 'active' : ''}`}
                                            >
                                                {fmt}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Location & Actions */}
                                <div className="ledger-control-group location-actions-group">
                                    <label className="control-label">Business Location</label>
                                    <div className="location-actions-wrapper">
                                        <div className="location-select-wrapper">
                                            <MapPin className="location-icon" size={16} />
                                            <select value={location} onChange={e => setLocation(e.target.value)} className="location-select">
                                                <option value="all">All locations</option>
                                                {businessLocations.map(loc => (
                                                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <button className="ledger-icon-btn">
                                            <Printer size={18} />
                                        </button>
                                        <button className="ledger-icon-btn">
                                            <Mail size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Store Name Right-Aligned (Subtle variant) */}
                            <div className="ledger-store-info">
                                <h4 className="store-name">{data.storeName}</h4>
                                <p className="store-address">
                                    {data.storeAddress}
                                </p>
                            </div>

                            {/* Customer & Account Summary Grid */}
                            <div className="ledger-summary-grid">

                                {/* Customer Column */}
                                <div className="summary-card customer-summary">
                                    <div className="card-header">
                                        To:
                                    </div>
                                    <div className="card-body">
                                        <strong className="summary-customer-name">{data.customer}</strong>
                                        <div className="summary-address">{data.address}</div>
                                        <div className="summary-contact-info"><strong>Email:</strong> {data.email}</div>
                                        <div className="summary-contact-info"><strong>Mobile:</strong> {data.mobile}</div>
                                        <div className="summary-contact-info"><strong>Tax number:</strong> {data.taxNumber}</div>
                                    </div>
                                </div>

                                {/* Account Summary / Statement Header Column */}
                                {ledgerFormat === 'Format 1' ? (
                                    <div className="summary-card account-summary">
                                        <div className="card-header">
                                            <span>Account Summary</span>
                                            <span className="summary-date-range">{dateFrom} - {dateTo}</span>
                                        </div>
                                        <div className="card-body">
                                            <table className="summary-table">
                                                <tbody>
                                                    <tr>
                                                        <td className="summary-label">Opening Balance</td>
                                                        <td className="summary-value">{formatCurrency(data.openingBalance)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="summary-label">Total Invoice</td>
                                                        <td className="summary-value">{formatCurrency(data.totalInvoice)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="summary-label">Total Paid</td>
                                                        <td className="summary-value paid">{formatCurrency(data.totalPaid)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="summary-label">Advance Balance</td>
                                                        <td className="summary-value">{formatCurrency(data.advanceBalance)}</td>
                                                    </tr>
                                                    <tr className="total-row">
                                                        <td className="summary-label">Balance due</td>
                                                        <td className="summary-value due">{formatCurrency(data.balanceDue)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="summary-card statement-summary" style={{ background: 'var(--etpos-card)', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', minWidth: '220px' }}>
                                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: 800, color: 'var(--etpos-text)' }}>Statement</h3>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Date</div>
                                        <div style={{ fontSize: '0.95rem', color: 'var(--etpos-text)', fontWeight: 700, marginTop: '0.2rem' }}>
                                            {dateFrom || '01/01/2026'} To {dateTo || '12/31/2026'}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Divider Title */}
                            <div className="ledger-divider">
                                <span className="divider-badge">
                                    Showing Ledger Data
                                </span>
                            </div>

                            {/* Tables Container Based on Selected Format */}
                            {isLoadingLedger ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>
                                    <div style={{ marginBottom: '0.5rem', fontSize: '1.25rem' }}>⏳</div>
                                    <div style={{ fontWeight: 600 }}>Loading ledger data...</div>
                                </div>
                            ) : (
                                <div>
                                    <div className="ledger-tables-container">
                                        {ledgerFormat === 'Format 1' && <Format1Table rows={data.detailedRows} />}
                                        {ledgerFormat === 'Format 2' && <Format2Table rows={data.statementRows} onViewPayment={handleViewPayment} />}
                                        {ledgerFormat === 'Format 3' && <Format3Table rows={ledgerData?.ledger || data.detailedRows} dateFrom={dateFrom} dateTo={dateTo} salesData={salesData} />}
                                    </div>

                                    {/* Aging Summary Bar for Format 2 & Format 3 */}
                                    {(ledgerFormat === 'Format 2' || ledgerFormat === 'Format 3') && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', background: 'var(--etpos-card)', padding: '1rem 1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '1.25rem', textAlign: 'center' }}>
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#1e293b', fontWeight: 700, marginBottom: '0.25rem' }}>Current</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(data.balanceDue)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: 700, marginBottom: '0.25rem' }}>1-30 DAYS PAST DUE</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#16a34a' }}>{formatCurrency(0)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#d97706', fontWeight: 700, marginBottom: '0.25rem' }}>30-60 DAYS PAST DUE</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#d97706' }}>{formatCurrency(0)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#ea580c', fontWeight: 700, marginBottom: '0.25rem' }}>60-90 DAYS PAST DUE</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#ea580c' }}>{formatCurrency(0)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 700, marginBottom: '0.25rem' }}>OVER 90 DAYS PAST DUE</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(0)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 800, marginBottom: '0.25rem' }}>AMOUNT DUE</div>
                                                <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>{formatCurrency(data.balanceDue)}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    )}
                </div>
            </div>

            {/* View Payment Sub-Modal */}
            {showViewPaymentsModal && (
                <ViewPaymentsModal
                    sale={sale}
                    transaction={selectedPaymentRow}
                    onClose={() => setShowViewPaymentsModal(false)}
                />
            )}

            {/* Add Payment Sub-Modal */}
            {showAddPaymentModal && selectedSaleRow && (
                <AddPaymentModal
                    sale={selectedSaleRow}
                    onClose={() => {
                        setShowAddPaymentModal(false);
                        setSelectedSaleRow(null);
                    }}
                    onSuccess={() => {
                        setShowAddPaymentModal(false);
                        setSelectedSaleRow(null);
                        if (selectedContact?.id) {
                            apiService.getSales({ contact_id: selectedContact.id, per_page: 100 })
                                .then(r => {
                                    setSalesData(r.data?.data || r.data || []);
                                    setSalesTotal(r.data?.meta?.total || (r.data?.data || r.data || []).length);
                                })
                                .catch(() => { });
                            apiService.getPurchases({ supplier_id: selectedContact.id, per_page: 100 })
                                .then(r => setPurchasesData(r?.data || r || []))
                                .catch(() => { });
                        }
                    }}
                />
            )}

            {/* View Sale Modal */}
            {showViewModal && selectedSaleForView && (
                <ViewSaleModal
                    sale={selectedSaleForView}
                    onClose={() => {
                        setShowViewModal(false);
                        setSelectedSaleForView(null);
                    }}
                    onPrintInvoice={(s) => {
                        const url = s?.apiData?.invoice_url || s?.invoiceUrl || s?.invoice_url;
                        if (url) window.open(url, '_blank');
                        else toast.error('Invoice URL not available');
                    }}
                    onPackingSlip={() => toast.info('Packing slip coming soon')}
                />
            )}

            {/* Edit Shipping Modal */}
            {showEditShippingModal && selectedSaleForShipping && (
                <EditShippingModal
                    sale={selectedSaleForShipping}
                    onClose={() => {
                        setShowEditShippingModal(false);
                        setSelectedSaleForShipping(null);
                    }}
                    onUpdate={() => {
                        // Refresh sales data for current contact
                        if (selectedContact?.id) {
                            apiService.getSales({ contact_id: selectedContact.id, per_page: 100 })
                                .then(r => setSalesData(r.data?.data || r.data || []))
                                .catch(() => { });
                        }
                    }}
                />
            )}

            {/* Sell Return Modal */}
            {showSellReturnModal && selectedSaleForReturn && (
                <SellReturnModal
                    sale={selectedSaleForReturn}
                    locations={[]}
                    onClose={() => {
                        setShowSellReturnModal(false);
                        setSelectedSaleForReturn(null);
                    }}
                    onSuccess={() => {
                        setShowSellReturnModal(false);
                        setSelectedSaleForReturn(null);
                        if (selectedContact?.id) {
                            apiService.getSales({ contact_id: selectedContact.id, per_page: 100 })
                                .then(r => setSalesData(r.data?.data || r.data || []))
                                .catch(() => { });
                        }
                    }}
                />
            )}

            {/* View Purchase Modal */}
            {showViewPurchaseModal && selectedPurchaseForView && (
                <ViewPurchaseModal
                    purchase={selectedPurchaseForView}
                    isLoading={isLoadingViewPurchase}
                    onClose={() => { setShowViewPurchaseModal(false); setSelectedPurchaseForView(null); }}
                    onPrint={(purchase) => {
                        const buildPurchaseInvoiceData = (p) => ({
                            invoiceNumber: p.invoice_no || p.ref_no || '--',
                            invoiceDate: p.transaction_date ? new Date(p.transaction_date).toLocaleDateString() : '--',
                            customerName: p.contact?.name || p.supplier || '--',
                            items: (p.purchase_lines || []).map(line => ({
                                sku: line.product?.sku || line.sub_sku || '',
                                name: line.product?.name || ('Product #' + line.product_id),
                                quantity: parseFloat(line.quantity || 0),
                                unitPrice: parseFloat(line.purchase_price || 0),
                                total: parseFloat(line.quantity || 0) * parseFloat(line.purchase_price || 0),
                            })),
                            payments: (p.payment_lines || []).map(pay => ({
                                method: pay.method,
                                amount: parseFloat(pay.amount || 0),
                                date: pay.paid_on && !pay.paid_on.startsWith('0000') ? new Date(pay.paid_on).toLocaleDateString() : '--',
                            })),
                            subtotal: parseFloat(p.total_before_tax || p.final_total || 0),
                            discount: parseFloat(p.discount_amount || 0),
                            tax: parseFloat(p.tax_amount || 0),
                            total: parseFloat(p.final_total || 0),
                            totalPaid: (p.payment_lines || []).reduce((s, pay) => s + parseFloat(pay.amount || 0), 0),
                            amountDue: parseFloat(p.payment_due || 0),
                            notes: p.additional_notes || '',
                        });
                        invoiceGenerator.printInvoicePDF(buildPurchaseInvoiceData(purchase));
                    }}
                />
            )}

            {/* Update Purchase Status Modal */}
            {showUpdatePurchaseStatusModal && selectedPurchaseForStatus && (
                <UpdatePurchaseStatusModal
                    show={showUpdatePurchaseStatusModal}
                    onClose={() => { setShowUpdatePurchaseStatusModal(false); setSelectedPurchaseForStatus(null); }}
                    purchase={selectedPurchaseForStatus}
                    onSuccess={() => {
                        setShowUpdatePurchaseStatusModal(false);
                        setSelectedPurchaseForStatus(null);
                        if (selectedContact?.id) {
                            apiService.getPurchases({ supplier_id: selectedContact.id, per_page: 100 })
                                .then(r => setPurchasesData(r?.data || r || []))
                                .catch(() => { });
                        }
                    }}
                />
            )}

            {/* Payment Edit Modal */}
            {showPaymentModal && selectedPayment && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: '1rem' }}>
                    <div style={{ background: 'var(--etpos-card)', width: '100%', maxWidth: '680px', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                        <div style={{ padding: '0.9rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--etpos-text)', fontWeight: 600 }}>Edit Payment</h3>
                            <button onClick={() => { setShowPaymentModal(false); setSelectedPayment(null); }} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
                        </div>
                        <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>
                            {/* Info cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
                                <div style={{ background: 'var(--etpos-bg)', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                                    <div><strong>Customer:</strong> {selectedContact?.name || selectedContact?.customer || '--'}</div>
                                    <div><strong>Ref No.:</strong> {selectedPayment.ref_no || selectedPayment.payment_ref_no || '--'}</div>
                                </div>
                                <div style={{ background: 'var(--etpos-bg)', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                                    <div><strong>Location:</strong> {(typeof selectedPayment.location === 'object' ? selectedPayment.location?.name : selectedPayment.location) || '--'}</div>
                                </div>
                                <div style={{ background: 'var(--etpos-bg)', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                                    <div><strong>Amount:</strong> ${Number(selectedPayment.debit || selectedPayment.credit || selectedPayment.amount || 0).toFixed(2)}</div>
                                    <div><strong>Method:</strong> {selectedPayment.payment_method || selectedPayment.method || '--'}</div>
                                </div>
                            </div>

                            {/* Edit form */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '1rem', background: 'var(--etpos-bg)' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Amount:*</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            id="edit-payment-amount"
                                            defaultValue={Number(selectedPayment.debit || selectedPayment.credit || selectedPayment.amount || 0).toFixed(2)}
                                            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Method:*</label>
                                        <select
                                            id="edit-payment-method"
                                            defaultValue={selectedPayment.payment_method || selectedPayment.method || 'cash'}
                                            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="card">Card</option>
                                            <option value="cheque">Cheque</option>
                                            <option value="bank_transfer">Bank Transfer</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Paid On:*</label>
                                        <input
                                            type="datetime-local"
                                            id="edit-payment-paidon"
                                            defaultValue={selectedPayment.paid_on || selectedPayment.date || ''}
                                            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Note:</label>
                                        <input
                                            type="text"
                                            id="edit-payment-note"
                                            defaultValue={selectedPayment.note || ''}
                                            placeholder="Optional note"
                                            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Reference No:</label>
                                        <input
                                            type="text"
                                            id="edit-payment-ref"
                                            defaultValue={selectedPayment.payment_ref_no || selectedPayment.ref_no || ''}
                                            placeholder="Reference number"
                                            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '0.9rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                            <button
                                onClick={async () => {
                                    const pid = selectedPayment.payment_id || selectedPayment.id;
                                    if (!pid) { toast.error('Payment ID not found'); return; }
                                    const amount = document.getElementById('edit-payment-amount').value;
                                    const method = document.getElementById('edit-payment-method').value;
                                    const note = document.getElementById('edit-payment-note').value;
                                    const paidOn = document.getElementById('edit-payment-paidon').value;
                                    const refNo = document.getElementById('edit-payment-ref').value;
                                    try {
                                        await apiService.updateSellPayment(pid, {
                                            amount: parseFloat(amount),
                                            method,
                                            note,
                                            paid_on: paidOn || undefined,
                                            payment_ref_no: refNo || undefined
                                        });
                                        setPaymentsData(prev => prev.map(p => {
                                            if ((p.payment_id || p.id) !== pid) return p;
                                            return { ...p, amount: parseFloat(amount), payment_method: method, method, note, paid_on: paidOn, payment_ref_no: refNo, ref_no: refNo };
                                        }));
                                        toast.success('Payment updated successfully');
                                        setShowPaymentModal(false);
                                        setSelectedPayment(null);
                                    } catch (err) {
                                        /* console.error('Update payment failed:', err); */
                                        toast.error('Failed to update payment');
                                    }
                                }}
                                style={{ background: '#581c87', color: 'var(--etpos-card)', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                            >Save</button>
                            <button
                                onClick={() => { setShowPaymentModal(false); setSelectedPayment(null); }}
                                style={{ background: '#374151', color: 'var(--etpos-card)', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                            >Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pay Contact Due Modal */}
            {showPayContactDueModal && selectedContact && createPortal(
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000000, padding: '1rem' }}>
                    <div style={{ background: 'var(--etpos-card)', width: '100%', maxWidth: '720px', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                        <div style={{ padding: '0.9rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--etpos-text)', fontWeight: 600 }}>Pay Due Amount</h3>
                            <button onClick={() => setShowPayContactDueModal(false)} disabled={payContactDueLoading} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
                        </div>
                        <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>
                            {/* Info cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
                                <div style={{ background: 'var(--etpos-bg)', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                                    <div><strong>{selectedContact.type === 'supplier' ? 'Supplier' : 'Customer'}:</strong> {selectedContact.name}</div>
                                    {selectedContact.supplier_business_name && <div><strong>Business:</strong> {selectedContact.supplier_business_name}</div>}
                                </div>
                                <div style={{ background: 'var(--etpos-bg)', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                                    <div><strong>Total Due:</strong> <span style={{ color: '#dc2626', fontWeight: 700 }}>${data.balanceDue.toFixed(2)}</span></div>
                                    <div><strong>Type:</strong> {selectedContact.type === 'supplier' ? 'Purchase Due' : 'Sale Due'}</div>
                                </div>
                            </div>

                            {/* Payment form */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '1rem', background: 'var(--etpos-bg)' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Method:*</label>
                                        <select style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%', background: 'var(--etpos-card)' }} value={payContactDueData.method} onChange={e => handlePayContactDueChange('method', e.target.value)} disabled={payContactDueLoading}>
                                            {payContactDueMethods.map(m => (
                                                <option key={m.id} value={m.id}>{m.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Paid On:*</label>
                                        <input type="datetime-local" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%', background: 'var(--etpos-card)' }} value={payContactDueData.paidOn} onChange={e => handlePayContactDueChange('paidOn', e.target.value)} disabled={payContactDueLoading} />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Amount:*</label>
                                        <input type="number" step="0.01" min="0" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%', background: 'var(--etpos-card)' }} value={payContactDueData.amount} onChange={e => handlePayContactDueChange('amount', e.target.value)} disabled={payContactDueLoading} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Account:</label>
                                        <select style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%', background: 'var(--etpos-card)' }} value={payContactDueData.accountId} onChange={e => handlePayContactDueChange('accountId', e.target.value)} disabled={payContactDueLoading}>
                                            <option value="">None</option>
                                            {payContactDueAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>{acc.name || acc.account_name || acc.display_name || `Account #${acc.id}`}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Note:</label>
                                        <input type="text" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%', background: 'var(--etpos-card)' }} value={payContactDueData.note} onChange={e => handlePayContactDueChange('note', e.target.value)} placeholder="Optional note" disabled={payContactDueLoading} />
                                    </div>
                                </div>

                                {payContactDueData.method === 'card' && (
                                    <div style={{ background: 'var(--etpos-border)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                            <div>
                                                <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Number:*</label>
                                                <input type="text" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardNumber} onChange={e => handlePayContactDueChange('cardNumber', e.target.value)} disabled={payContactDueLoading} />
                                            </div>
                                            <div>
                                                <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Holder:*</label>
                                                <input type="text" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardHolderName} onChange={e => handlePayContactDueChange('cardHolderName', e.target.value)} disabled={payContactDueLoading} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                                            <div>
                                                <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Type</label>
                                                <select style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardType} onChange={e => handlePayContactDueChange('cardType', e.target.value)} disabled={payContactDueLoading}>
                                                    <option value="visa">Visa</option>
                                                    <option value="mastercard">Mastercard</option>
                                                    <option value="amex">Amex</option>
                                                    <option value="discover">Discover</option>
                                                </select>
                                            </div>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Month</label><input type="text" maxLength="2" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardMonth} onChange={e => handlePayContactDueChange('cardMonth', e.target.value)} placeholder="MM" disabled={payContactDueLoading} /></div>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Year</label><input type="text" maxLength="4" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardYear} onChange={e => handlePayContactDueChange('cardYear', e.target.value)} placeholder="YYYY" disabled={payContactDueLoading} /></div>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>CVV</label><input type="text" maxLength="4" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardSecurity} onChange={e => handlePayContactDueChange('cardSecurity', e.target.value)} placeholder="123" disabled={payContactDueLoading} /></div>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Txn #</label><input type="text" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.cardTransactionNumber} onChange={e => handlePayContactDueChange('cardTransactionNumber', e.target.value)} disabled={payContactDueLoading} /></div>
                                        </div>
                                    </div>
                                )}

                                {payContactDueData.method === 'cheque' && (
                                    <div style={{ background: 'var(--etpos-border)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Cheque Number</label><input type="text" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.chequeNumber} onChange={e => handlePayContactDueChange('chequeNumber', e.target.value)} disabled={payContactDueLoading} /></div>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Cheque Expiry</label><input type="date" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.chequeExpiryDate} onChange={e => handlePayContactDueChange('chequeExpiryDate', e.target.value)} disabled={payContactDueLoading} /></div>
                                            <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Bank Submission Date</label><input type="date" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%' }} value={payContactDueData.bankSubmissionDate} onChange={e => handlePayContactDueChange('bankSubmissionDate', e.target.value)} disabled={payContactDueLoading} /></div>
                                        </div>
                                    </div>
                                )}

                                {payContactDueData.method === 'bank_transfer' && (
                                    <div style={{ background: 'var(--etpos-border)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                                        <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Bank Account Number:*</label>
                                        <input type="text" style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', maxWidth: '320px', width: '100%' }} value={payContactDueData.bankAccountNumber} onChange={e => handlePayContactDueChange('bankAccountNumber', e.target.value)} placeholder="Enter bank account number" disabled={payContactDueLoading} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ padding: '0.9rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                            <button onClick={handleSavePayContactDue} disabled={payContactDueLoading} style={{ background: payContactDueLoading ? '#9ca3af' : '#581c87', color: 'var(--etpos-card)', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: payContactDueLoading ? 'not-allowed' : 'pointer', minWidth: '90px' }}>{payContactDueLoading ? 'Saving...' : 'Save'}</button>
                            <button onClick={() => setShowPayContactDueModal(false)} disabled={payContactDueLoading} style={{ background: '#374151', color: 'var(--etpos-card)', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', minWidth: '90px' }}>Close</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default LedgerModal;
